import { RoleService } from './role/role.service';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UserRole } from '../services/auth/auth.role';
import { AuthService } from '../services/auth/auth.service';
import { NavigationItem, DropdownItem } from 'htx-ui-library';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

interface NavigationRoute extends NavigationItem {
  visibleForRoles?: UserRole[];
  visibleInExtraMenu?: boolean;
}

// Role constants for routes
export const PARTNERNETWORK_ROLES: UserRole[] = [UserRole.OrganizationUser];

export const USE_CASES_ROLES: UserRole[] = [
  UserRole.OrganizationUser,
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const ADMIN_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const DATASPACE_AUTHORITY_ROLES: UserRole[] = [UserRole.DataspaceAuthority];

export const ORGANIZATION_EDIT_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const MEMBER_EDIT_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const MEMBER_ADD_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const APP_MANAGEMENT_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative
];

export const AUDIT_LOG_ROLES: UserRole[] = [
  UserRole.OrganizationAdmin,
  UserRole.OrganizationRepresentative,
  UserRole.DataspaceAuthority
];

export const DSA_MANAGEMENT_ROLES: UserRole[] = [UserRole.DataspaceAuthority];

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private router = inject(Router);
  private roleService = inject(RoleService);
  private authService = inject(AuthService);
  private transloco = inject(TranslocoService);

  private routes: NavigationRoute[] = [
    {
      label: 'app.title',
      icon: 'home',
      onItemClick: () => {
        this.router.navigate(['/']);
      }
    },
    {
      label: 'menu.participate',
      icon: 'handshake',
      onItemClick: () => {}
    },
    {
      label: 'menu.explore',
      icon: 'explore',
      onItemClick: () => {}
    },
    {
      label: 'menu.usecases',
      icon: 'lightbulb',
      visibleForRoles: USE_CASES_ROLES,
      dropdownItems: [
        {
          label: 'usecases.supplyChain.title',
          icon: 'account_tree',
          value: {
            onItemClick: () => this.router.navigate(['/use-cases/supply-chain'])
          }
        },
        {
          label: 'usecases.shortageManagement.title',
          icon: 'show_chart',
          value: {
            onItemClick: () => this.router.navigate(['/use-cases/shortage-management'])
          }
        },
        {
          label: 'usecases.productPassport.title',
          icon: 'co2',
          value: {
            onItemClick: () => this.router.navigate(['/use-cases/product-passport'])
          }
        }
      ]
    },
    {
      label: 'menu.partnernetwork',
      icon: 'group',
      onItemClick: () => {
        this.router.navigate(['/partnernetwork']);
      },
      visibleForRoles: PARTNERNETWORK_ROLES
    },
    {
      label: 'menu.admin.dashboard',
      icon: 'dashboard',
      onItemClick: () => {
        this.router.navigate(['/admin/dashboard']);
      },
      visibleForRoles: DATASPACE_AUTHORITY_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.requests',
      icon: 'description',
      onItemClick: () => {
        this.router.navigate(['/admin/requests']);
      },
      visibleForRoles: DATASPACE_AUTHORITY_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.organizations',
      icon: 'business',
      onItemClick: () => {
        this.router.navigate(['/admin/organizations']);
      },
      visibleForRoles: ADMIN_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.appManagement',
      icon: 'apps',
      onItemClick: () => {
        this.router.navigate(['/app-management']);
      },
      visibleForRoles: APP_MANAGEMENT_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.users',
      icon: 'group',
      onItemClick: () => {
        this.router.navigate(['/admin/users']);
      },
      visibleForRoles: DSA_MANAGEMENT_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.emailTemplates',
      icon: 'mail',
      onItemClick: () => {
        this.router.navigate(['/admin/email-templates']);
      },
      visibleForRoles: DSA_MANAGEMENT_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.auditLog',
      icon: 'history',
      onItemClick: () => {
        this.router.navigate(['/admin/audit-log']);
      },
      visibleForRoles: AUDIT_LOG_ROLES,
      visibleInExtraMenu: true
    },
    {
      label: 'menu.admin.infrastructure',
      icon: 'hub',
      onItemClick: () => {
        this.router.navigate(['/admin/infrastructure']);
      },
      visibleForRoles: DSA_MANAGEMENT_ROLES,
      visibleInExtraMenu: true
    }
  ];

  getNavigationRoutes$(): Observable<NavigationItem[]> {
    return this.roleService.getUserRoles$().pipe(
      switchMap((userRoles) => {
        const keys = this.routes
          .filter((route) => {
            if (route.visibleInExtraMenu) return false;
            if (route.visibleForRoles) {
              return route.visibleForRoles.some((role) => userRoles.includes(role));
            }
            return true;
          })
          .map((route) => route.label);

        return this.transloco.selectTranslate(keys).pipe(
          map((translations) => {
            const filtered = this.routes.filter((route) => {
              if (route.visibleInExtraMenu) return false;
              if (route.visibleForRoles) {
                return route.visibleForRoles.some((role) => userRoles.includes(role));
              }
              return true;
            });

            return filtered.map((route) => ({
              ...route,
              label: this.transloco.translate(route.label),
              dropdownItems: route.dropdownItems?.map((item) => ({
                ...item,
                label: this.transloco.translate(item.label)
              }))
            }));
          })
        );
      })
    );
  }

  getAdminMenuRoutes$(): Observable<DropdownItem[]> {
    return this.roleService.getUserRoles$().pipe(
      switchMap((userRoles) => {
        if (
          !userRoles.includes(UserRole.OrganizationAdmin) &&
          !userRoles.includes(UserRole.OrganizationRepresentative) &&
          !userRoles.includes(UserRole.DataspaceAuthority)
        )
          return of([]);

        const keys = this.routes
          .filter(
            (route) =>
              route.visibleInExtraMenu &&
              route.visibleForRoles?.some((role) => userRoles.includes(role))
          )
          .map((route) => route.label);

        return this.transloco.selectTranslate(keys).pipe(
          map((translations) => {
            const filtered = this.routes.filter(
              (route) =>
                route.visibleInExtraMenu &&
                route.visibleForRoles?.some((role) => userRoles.includes(role))
            );
            return filtered.map((route) => {
              let label = this.transloco.translate(route.label);

              // For organizations route: show 'menu.admin.organization' if only OrganizationAdmin (not DataspaceAuthority)
              if (
                route.label === 'menu.admin.organizations' &&
                userRoles.includes(UserRole.OrganizationAdmin) &&
                !userRoles.includes(UserRole.DataspaceAuthority)
              ) {
                label = this.transloco.translate('menu.admin.organization');
              }

              return {
                ...route,
                label,
                icon: (route as any)['icon'] ?? '',
                value: this.extractRoutePath(route)
              };
            });
          })
        );
      })
    );
  }

  getProfileMenuRoutes$(): Observable<DropdownItem[]> {
    return this.roleService.getUserRoles$().pipe(
      switchMap((userRoles) => {
        const isAdmin =
          userRoles.includes(UserRole.OrganizationAdmin) ||
          userRoles.includes(UserRole.OrganizationRepresentative) ||
          userRoles.includes(UserRole.DataspaceAuthority);

        const adminRoutes = isAdmin
          ? this.routes.filter(
              (route) =>
                route.visibleInExtraMenu &&
                route.visibleForRoles?.some((role) => userRoles.includes(role))
            )
          : [];

        // Dashboard und Organisationen bleiben auf oberster Ebene,
        // die restlichen Admin-Einträge kommen ins "Verwaltung"-Unter-Dropdown.
        const TOP_LEVEL_ADMIN_LABELS = ['menu.admin.dashboard', 'menu.admin.organizations'];

        const keysToTranslate = [
          'menu.profile',
          ...adminRoutes.map((r) => r.label),
          ...(isAdmin ? ['menu.admin.organization', 'menu.admin.management'] : []),
          'menu.logout'
        ];

        return this.transloco.selectTranslate(keysToTranslate).pipe(
          map(() => {
            const profileItem = {
              label: this.transloco.translate('menu.profile'),
              icon: 'manage_accounts',
              value: '/profile',
              onItemClick: () => this.router.navigate(['/profile'])
            };

            const mapAdminRoute = (route: NavigationRoute): DropdownItem => {
              let label = this.transloco.translate(route.label);
              if (
                route.label === 'menu.admin.organizations' &&
                (userRoles.includes(UserRole.OrganizationAdmin) ||
                  userRoles.includes(UserRole.OrganizationRepresentative)) &&
                !userRoles.includes(UserRole.DataspaceAuthority)
              ) {
                label = this.transloco.translate('menu.admin.organization');
              }
              return {
                ...route,
                label,
                icon: (route as any)['icon'] ?? '',
                value: this.extractRoutePath(route)
              };
            };

            const topLevelAdminItems = adminRoutes
              .filter((route) => TOP_LEVEL_ADMIN_LABELS.includes(route.label))
              .map(mapAdminRoute);
            const managementChildren = adminRoutes
              .filter((route) => !TOP_LEVEL_ADMIN_LABELS.includes(route.label))
              .map(mapAdminRoute);

            // Logout-Eintrag ergänzen
            const logoutItem = {
              label: this.transloco.translate('menu.logout'),
              icon: 'logout',
              value: '__logout__',
              onItemClick: () => this.authService.logout()
            };

            const managementItem: DropdownItem = {
              label: this.transloco.translate('menu.admin.management'),
              icon: 'settings',
              value: '__management__',
              children: managementChildren
            } as unknown as DropdownItem;

            return [
              profileItem as unknown as DropdownItem,
              ...topLevelAdminItems,
              ...(managementChildren.length ? [managementItem] : []),
              logoutItem
            ];
          })
        );
      })
    );
  }

  /**
   * Redirects logged-in users to the home page.
   * Used to prevent authenticated users from accessing public-only pages like registration.
   * @returns true if the user was redirected, false otherwise
   */
  redirectIfLoggedIn(): boolean {
    if (this.authService.isLoggedIn$.value) {
      void this.router.navigate(['/home']);
      return true;
    }
    return false;
  }

  /**
   * Get required roles for partnernetwork route
   */
  getAccessRoles(route: string): UserRole[] {
    if (route === 'use-cases') {
      return [];
    }
    if (route === 'partnernetwork') {
      return PARTNERNETWORK_ROLES;
    }
    if (route === 'admin/dashboard') {
      return DATASPACE_AUTHORITY_ROLES;
    }
    if (route === 'admin/organizations') {
      return ADMIN_ROLES;
    }
    if (route === 'admin/requests') {
      return DATASPACE_AUTHORITY_ROLES;
    }
    if (route === 'admin/organizations/edit') {
      return ORGANIZATION_EDIT_ROLES;
    }
    if (route === 'admin/organizations/members/edit') {
      return MEMBER_EDIT_ROLES;
    }
    if (route === 'admin/organizations/members/add') {
      return MEMBER_ADD_ROLES;
    }
    if (route === 'app-management') {
      return APP_MANAGEMENT_ROLES;
    }
    if (route === 'admin/audit-log') {
      return AUDIT_LOG_ROLES;
    }
    if (route === 'admin/users') {
      return DSA_MANAGEMENT_ROLES;
    }
    if (route === 'admin/email-templates') {
      return DSA_MANAGEMENT_ROLES;
    }
    if (route === 'admin/infrastructure') {
      return DSA_MANAGEMENT_ROLES;
    }
    return [];
  }

  /**
   * Helper to extract the route path from a NavigationRoute's onItemClick navigation.
   * Assumes the route is navigated via router.navigate([path]) in onItemClick.
   * If not available, returns an empty string.
   */
  private extractRoutePath(route: NavigationRoute): string {
    // This is a placeholder. In a real implementation, store the path as a property on the route.
    // For now, try to infer from the navigation function string (not robust, but works for this pattern).
    if (route.onItemClick) {
      const fnStr = route.onItemClick.toString();
      const match = fnStr.match(/navigate\(\['"]([^'\"]+)['"]\)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return '';
  }
}
